import { NotFoundError } from "../../types";
import {
  InvitationEmailMismatchError,
  InvitationUnavailableError,
  SeatLimitError,
  ValidationError,
} from "../errors";
import { isEmail, normaliseEmail } from "../logic/codes";
import { checkSeats, invitationStatus } from "../logic/invitations";
import { sha256 } from "../logic/tokens";
import { IDENTITY_POLICY } from "../policy";
import type { IdentityApi, InvitationPreview, OrgRole } from "../types";
import { currentOrgView } from "./auth";
import {
  findOrg,
  requireOrg,
  requirePermission,
  requireSession,
  roleIn,
  type Ctx,
} from "./context";
import { recordAudit } from "./audit";
import type { StoredInvitation, StoredOrganisation } from "./state";
import { invitationView, memberView, orgDetails, orgUsage, sessionView } from "./views";

/** The current organisation: details, setup, members and invitations (FR-ADMIN-009, 026, 027). */

type OrgApi = Pick<
  IdentityApi,
  | "getOrganisation"
  | "updateOrganisation"
  | "completeOrganisationSetup"
  | "listMembers"
  | "listInvitations"
  | "invite"
  | "resendInvitation"
  | "revokeInvitation"
  | "getInvitation"
  | "acceptInvitation"
  | "declineInvitation"
>;

const ROLE_NAMES: Readonly<Record<OrgRole, string>> = {
  owner: "the Owner",
  admin: "an Admin",
  member: "a member",
  guest: "a guest",
};
const NAME_MAX = 80;
/** Short project IDs start with it (FR-PROJ-055), as the identity service checks it. */
const PROJECT_PREFIX = /^[A-Z]{1,6}$/;

function settingsOf(org: StoredOrganisation) {
  const { name, country, state, city, projectPrefix } = org;
  return { name, country, state, city, projectPrefix };
}

/**
 * Creates an invitation and "sends" its email. Live: our identity service checks seats, then calls the WorkOS
 * Invitations API, which emails the link; the mock puts the email in the demo inbox instead.
 */
export async function createInvitation(
  ctx: Ctx,
  input: { organisationId: string; email: string; role: OrgRole; invitedBy: string },
): Promise<StoredInvitation> {
  const org = findOrg(ctx, input.organisationId);
  if (org === undefined) throw new NotFoundError("Organisation");
  const token = ctx.token();
  const now = ctx.now();
  const invitation: StoredInvitation = {
    id: `inv-${ctx.token().slice(0, 12)}`,
    tokenHash: await sha256(token),
    organisationId: org.id,
    email: input.email,
    role: input.role,
    invitedBy: input.invitedBy,
    createdAt: now,
    expiresAt: now + IDENTITY_POLICY.invitationMs,
    lastSentAt: now,
    status: "pending",
  };
  ctx.state.invitations.push(invitation);
  const owner = input.role === "owner";
  ctx.state.outbox.push({
    id: `mail-${ctx.token().slice(0, 12)}`,
    to: input.email,
    subject: owner
      ? `Set up ${org.name} on Orthos`
      : `${input.invitedBy} invited you to ${org.name} on Orthos`,
    sentAt: now,
    body: [
      owner
        ? `Orthos has created ${org.name} for you. Accept this invitation to become its Owner and finish setting it up.`
        : `${input.invitedBy} invited you to join ${org.name} on Orthos as ${ROLE_NAMES[input.role]}.`,
      "Orthos is the design studio in a browser tab. This invitation lasts 14 days.",
    ],
    href: `/invite/${token}`,
    linkLabel: owner ? "Accept and set up" : "Accept invitation",
  });
  return invitation;
}

function cleanName(value: string | undefined, field: string): string | undefined {
  if (value === undefined) return undefined;
  const name = value.trim();
  if (name === "") throw new ValidationError(field, "This can't be empty.");
  if (name.length > NAME_MAX)
    throw new ValidationError(field, `Use ${String(NAME_MAX)} characters or fewer.`);
  return name;
}

export function createOrgApi(ctx: Ctx): OrgApi {
  const findByToken = async (token: string) => {
    const hash = await sha256(token);
    const invitation = ctx.state.invitations.find((inv) => inv.tokenHash === hash);
    if (invitation === undefined) throw new NotFoundError("This invitation");
    return invitation;
  };

  return {
    async getOrganisation() {
      await ctx.wait();
      return orgDetails(ctx, requireOrg(ctx).org);
    },

    async updateOrganisation(patch) {
      await ctx.wait();
      const context = requirePermission(ctx, "org.manage_settings");
      const { org } = context;
      const prefix = patch.projectPrefix?.trim();
      if (prefix !== undefined && !PROJECT_PREFIX.test(prefix))
        throw new ValidationError("projectPrefix", "Use 1 to 6 capital letters, such as NL.");
      const before = settingsOf(org);
      Object.assign(org, {
        ...(patch.name === undefined ? {} : { name: cleanName(patch.name, "name") }),
        ...(patch.country === undefined ? {} : { country: cleanName(patch.country, "country") }),
        ...(patch.state === undefined ? {} : { state: cleanName(patch.state, "state") }),
        ...(patch.city === undefined ? {} : { city: cleanName(patch.city, "city") }),
        ...(prefix === undefined ? {} : { projectPrefix: prefix }),
      });
      const after = settingsOf(org);
      if (JSON.stringify(before) !== JSON.stringify(after))
        recordAudit(
          ctx,
          context,
          "settings",
          "organisation.updated",
          { type: "organisation", id: org.id, name: org.name },
          { before, after },
        );
      ctx.commit();
      return orgDetails(ctx, org);
    },

    async completeOrganisationSetup(patch) {
      await ctx.wait();
      const { org, role } = requireOrg(ctx);
      if (role !== "owner")
        throw new ValidationError("role", "Only the Owner sets up the organisation.");
      Object.assign(org, {
        ...(patch.name === undefined ? {} : { name: cleanName(patch.name, "name") }),
        ...(patch.country === undefined ? {} : { country: cleanName(patch.country, "country") }),
        ...(patch.state === undefined ? {} : { state: cleanName(patch.state, "state") }),
        ...(patch.city === undefined ? {} : { city: cleanName(patch.city, "city") }),
        setupComplete: true,
      });
      ctx.commit();
      return currentOrgView(ctx);
    },

    async listMembers() {
      await ctx.wait();
      const { org, user, role } = requireOrg(ctx);
      return ctx.state.memberships
        .filter((m) => m.organisationId === org.id && (role !== "guest" || m.userId === user.id))
        .flatMap((m) => {
          const person = ctx.state.users.find((u) => u.id === m.userId);
          return person === undefined
            ? []
            : [memberView(person, m.role, m.joinedAt, person.id === user.id, m.suspended === true)];
        })
        .sort((a, b) => Number(b.you) - Number(a.you) || a.name.localeCompare(b.name));
    },

    async listInvitations() {
      await ctx.wait();
      const { org } = requirePermission(ctx, "org.manage_people");
      return ctx.state.invitations
        .filter((inv) => inv.organisationId === org.id)
        .map((inv) => invitationView(ctx, inv))
        .sort(
          (a, b) =>
            Number(b.status === "pending") - Number(a.status === "pending") ||
            b.createdAt.localeCompare(a.createdAt),
        );
    },

    async invite(rawEmails, role) {
      await ctx.wait();
      const { org, user } = requirePermission(ctx, "org.manage_people");
      const emails = [...new Set(rawEmails.map(normaliseEmail))];
      if (emails.length === 0)
        throw new ValidationError("emails", "Enter at least one email address.");
      const bad = emails.filter((email) => !isEmail(email));
      if (bad.length > 0)
        throw new ValidationError("emails", `Check these addresses: ${bad.join(", ")}`);
      const now = ctx.now();
      for (const email of emails) {
        const existing = ctx.state.users.find((u) => u.email === email);
        if (existing !== undefined && roleIn(ctx, existing.id, org.id) !== undefined) {
          throw new ValidationError("emails", `${email} is already a member.`);
        }
        if (
          ctx.state.invitations.some(
            (inv) =>
              inv.organisationId === org.id &&
              inv.email === email &&
              invitationStatus(inv, now) === "pending",
          )
        ) {
          throw new ValidationError(
            "emails",
            `${email} already has a pending invitation. Resend it from the list.`,
          );
        }
      }
      // Seat checks are ours, before any call to WorkOS (FR-ADMIN-027).
      const seats = checkSeats(
        org.allowances,
        orgUsage(ctx, org.id),
        emails.map(() => role),
      );
      if (!seats.ok) throw new SeatLimitError(seats.message);
      const created: StoredInvitation[] = [];
      for (const email of emails)
        created.push(
          await createInvitation(ctx, {
            organisationId: org.id,
            email,
            role,
            invitedBy: user.name,
          }),
        );
      ctx.commit();
      return created.map((inv) => invitationView(ctx, inv));
    },

    async resendInvitation(invitationId) {
      await ctx.wait();
      const { org, user } = requirePermission(ctx, "org.manage_people");
      const old = ctx.state.invitations.find(
        (inv) => inv.id === invitationId && inv.organisationId === org.id,
      );
      if (old === undefined) throw new NotFoundError("Invitation");
      const status = invitationStatus(old, ctx.now());
      if (status !== "pending" && status !== "expired")
        throw new InvitationUnavailableError(status);
      if (status === "expired") {
        const seats = checkSeats(org.allowances, orgUsage(ctx, org.id), [old.role]);
        if (!seats.ok) throw new SeatLimitError(seats.message);
      }
      // A resend sends a fresh link and restarts the 14 days; the old link stops working.
      const fresh = await createInvitation(ctx, {
        organisationId: org.id,
        email: old.email,
        role: old.role,
        invitedBy: user.name,
      });
      ctx.state.invitations = ctx.state.invitations.filter((inv) => inv.id !== old.id);
      fresh.id = old.id;
      fresh.createdAt = old.createdAt;
      ctx.commit();
      return invitationView(ctx, fresh);
    },

    async revokeInvitation(invitationId) {
      await ctx.wait();
      const { org } = requirePermission(ctx, "org.manage_people");
      const invitation = ctx.state.invitations.find(
        (inv) => inv.id === invitationId && inv.organisationId === org.id,
      );
      if (invitation === undefined) throw new NotFoundError("Invitation");
      if (invitation.status === "pending") invitation.status = "revoked";
      ctx.commit();
    },

    async getInvitation(token): Promise<InvitationPreview> {
      await ctx.wait();
      const invitation = await findByToken(token);
      const org = findOrg(ctx, invitation.organisationId);
      return {
        status: invitationStatus(invitation, ctx.now()),
        email: invitation.email,
        role: invitation.role,
        organisationName: org?.name ?? "An organisation",
        inviterName: invitation.invitedBy,
        expiresAt: new Date(invitation.expiresAt).toISOString(),
      };
    },

    async acceptInvitation(token) {
      await ctx.wait();
      const { session, user } = requireSession(ctx);
      const invitation = await findByToken(token);
      const status = invitationStatus(invitation, ctx.now());
      if (status !== "pending") throw new InvitationUnavailableError(status);
      if (invitation.email !== user.email) throw new InvitationEmailMismatchError(invitation.email);
      if (roleIn(ctx, user.id, invitation.organisationId) === undefined) {
        ctx.state.memberships.push({
          userId: user.id,
          organisationId: invitation.organisationId,
          role: invitation.role,
          joinedAt: ctx.now(),
        });
      }
      invitation.status = "accepted";
      session.organisationId = invitation.organisationId;
      ctx.commit();
      return sessionView(ctx, session, user);
    },

    async declineInvitation(token) {
      await ctx.wait();
      const invitation = await findByToken(token);
      const status = invitationStatus(invitation, ctx.now());
      if (status !== "pending") throw new InvitationUnavailableError(status);
      invitation.status = "declined";
      ctx.commit();
    },
  };
}
